<template>
  <div class="recovery-page" :class="{ 'dark-mode': isDark }" :dir="direction">
    <header class="recovery-header">
      <div class="recovery-brand">
        <span class="recovery-brand-icon"
          ><v-icon size="29">mdi-hospital-building</v-icon></span
        >
        <div>
          <strong>{{ copy.brand }}</strong>
          <small>{{ copy.brandSubtitle }}</small>
        </div>
      </div>
      <button type="button" class="recovery-language" @click="toggleLanguage">
        <v-icon size="18">mdi-translate</v-icon>
        {{ direction === "rtl" ? "العربية" : "English" }}
      </button>
    </header>

    <main class="recovery-main">
      <section class="recovery-card" aria-labelledby="recovery-title">
        <span class="recovery-emblem"
          ><v-icon size="32">mdi-lock-reset</v-icon></span
        >
        <h1 id="recovery-title">{{ copy.title }}</h1>
        <p class="recovery-intro">{{ copy.intro }}</p>

        <v-alert
          v-if="done"
          type="success"
          variant="tonal"
          class="recovery-alert"
          role="status"
        >
          {{ copy.success }}
        </v-alert>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="recovery-alert"
          role="alert"
        >
          {{ errorMessage }}
        </v-alert>

        <form
          v-if="token && !done"
          class="recovery-form"
          @submit.prevent="submit"
        >
          <label for="reset-email">{{ copy.email }}</label>
          <v-text-field
            id="reset-email"
            v-model="email"
            type="email"
            autocomplete="email"
            inputmode="email"
            prepend-inner-icon="mdi-email-outline"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="loading"
          />

          <label for="reset-password">{{ copy.password }}</label>
          <v-text-field
            id="reset-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="
              showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
            "
            @click:append-inner="showPassword = !showPassword"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="loading"
          />

          <label for="reset-confirmation">{{ copy.confirmation }}</label>
          <v-text-field
            id="reset-confirmation"
            v-model="confirmation"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            prepend-inner-icon="mdi-lock-check-outline"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="loading"
          />

          <v-btn
            type="submit"
            class="recovery-submit"
            block
            height="48"
            :loading="loading"
            :disabled="loading"
          >
            {{ copy.submit }}
          </v-btn>
        </form>

        <router-link
          class="recovery-back"
          :to="done ? '/login' : '/forgot-password'"
        >
          <v-icon size="18">mdi-arrow-left</v-icon>
          {{ done ? copy.login : copy.requestAgain }}
        </router-link>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import api from "@/plugins/api";

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const token = ref(
  typeof route.query.token === "string" ? route.query.token : ""
);
const email = ref(
  typeof route.query.email === "string" ? route.query.email : ""
);
const password = ref("");
const confirmation = ref("");
const showPassword = ref(false);
const loading = ref(false);
const done = ref(false);
const errorMessage = ref("");
const isDark = ref(false);
const direction = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
const copy = computed(() =>
  direction.value === "rtl"
    ? {
        brand: "نظام تقييم المرافق الصحية",
        brandSubtitle: "استعادة الوصول إلى الحساب",
        title: "تعيين كلمة مرور جديدة",
        intro: "اختر كلمة مرور من 8 أحرف على الأقل، ثم أكدها.",
        email: "البريد الإلكتروني",
        password: "كلمة المرور الجديدة",
        confirmation: "تأكيد كلمة المرور",
        submit: "حفظ كلمة المرور",
        success:
          "تم تغيير كلمة المرور وإبطال جلسات الدخول القديمة. سجّل الدخول بكلمة المرور الجديدة.",
        login: "تسجيل الدخول",
        requestAgain: "طلب رابط استعادة جديد",
        invalid:
          "رابط الاستعادة غير صالح أو انتهت صلاحيته. اطلب رابطًا جديدًا.",
        required: "يرجى إدخال بريد إلكتروني صحيح.",
        tooShort: "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.",
        mismatch: "تأكيد كلمة المرور غير مطابق.",
        failure: "تعذر تغيير كلمة المرور الآن. حاول مرة أخرى.",
      }
    : {
        brand: "Health Facility Assessment System",
        brandSubtitle: "Account recovery",
        title: "Set a new password",
        intro: "Choose a password with at least 8 characters, then confirm it.",
        email: "Email address",
        password: "New password",
        confirmation: "Confirm password",
        submit: "Save password",
        success:
          "Your password was changed and previous sessions were revoked. Sign in with the new password.",
        login: "Sign in",
        requestAgain: "Request a new reset link",
        invalid: "This reset link is invalid or expired. Request a new one.",
        required: "Enter a valid email address.",
        tooShort: "Use at least 8 characters for your password.",
        mismatch: "The passwords do not match.",
        failure: "Could not change the password right now. Please try again.",
      }
);

function toggleLanguage() {
  locale.value = direction.value === "rtl" ? "en" : "ar";
  localStorage.setItem("lang", locale.value);
  document.documentElement.lang = locale.value;
  document.documentElement.dir = direction.value;
  document.body.dir = direction.value;
}

function clearStoredSessionForThisUser() {
  let currentUser;
  try {
    currentUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    currentUser = null;
  }
  if (
    String(currentUser?.email || "").toLowerCase() !==
    email.value.trim().toLowerCase()
  )
    return;
  for (const key of [
    "token",
    "user",
    "role",
    "permissions",
    "isAuthenticated",
    "token_expires_at",
  ]) {
    localStorage.removeItem(key);
  }
}

async function submit() {
  errorMessage.value = "";
  if (!token.value) {
    errorMessage.value = copy.value.invalid;
    return;
  }
  const emailValue = email.value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
    errorMessage.value = copy.value.required;
    return;
  }
  if (password.value.length < 8) {
    errorMessage.value = copy.value.tooShort;
    return;
  }
  if (password.value !== confirmation.value) {
    errorMessage.value = copy.value.mismatch;
    return;
  }

  loading.value = true;
  try {
    await api.post("/reset-password", {
      token: token.value,
      email: emailValue,
      password: password.value,
      password_confirmation: confirmation.value,
    });
    clearStoredSessionForThisUser();
    password.value = "";
    confirmation.value = "";
    token.value = "";
    done.value = true;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.code === "INVALID_RESET_TOKEN"
        ? copy.value.invalid
        : copy.value.failure;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  const savedLanguage = localStorage.getItem("lang");
  if (savedLanguage === "ar" || savedLanguage === "en")
    locale.value = savedLanguage;
  isDark.value = localStorage.getItem("theme") === "dark";
  document.documentElement.lang = locale.value;
  document.documentElement.dir = direction.value;
  document.body.dir = direction.value;

  if (!token.value) {
    errorMessage.value = copy.value.invalid;
  } else {
    // Keep the token in component memory, then remove it from browser history.
    const query = { ...route.query };
    delete query.token;
    void router.replace({ path: route.path, query, hash: route.hash });
  }
});
</script>

<style scoped>
.recovery-page {
  --recovery-navy: #173f6b;
  --recovery-gold: #c49a3a;
  box-sizing: border-box;
  min-height: 100vh;
  background: linear-gradient(180deg, #eef3f8 0%, #f7f9fc 100%);
  color: #0f172a;
  font-family: "Cairo", "Inter", sans-serif;
}

.recovery-page *,
.recovery-page *::before,
.recovery-page *::after {
  box-sizing: border-box;
}
.recovery-page[dir="rtl"] {
  direction: rtl;
  text-align: right;
}
.recovery-page[dir="ltr"] {
  direction: ltr;
  text-align: left;
}

.recovery-header {
  min-height: 76px;
  padding: 12px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  background: var(--recovery-navy);
  border-bottom: 4px solid var(--recovery-gold);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}

.recovery-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.recovery-brand-icon {
  display: grid;
  place-items: center;
  flex: 0 0 48px;
  height: 44px;
  border-radius: 11px;
  background: #fff;
  color: var(--recovery-navy);
}
.recovery-brand strong {
  display: block;
  overflow-wrap: anywhere;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.5;
}
.recovery-brand small {
  display: block;
  color: #d9e6f3;
  font-size: 11px;
  line-height: 1.5;
}
.recovery-language {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 39px;
  padding: 7px 11px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 10px;
  color: #fff;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
}
.recovery-language:hover {
  background: rgba(255, 255, 255, 0.12);
}

.recovery-main {
  min-height: calc(100vh - 76px);
  padding: 36px 16px 60px;
  display: grid;
  place-items: center;
}
.recovery-card {
  width: min(100%, 480px);
  padding: 30px;
  border: 1px solid #dce5ef;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.1);
}
.recovery-emblem {
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  margin-bottom: 20px;
  border-radius: 17px;
  color: var(--recovery-navy);
  background: #edf3f9;
}
.recovery-card h1 {
  margin: 0 0 8px;
  font-size: 23px;
  line-height: 1.5;
  font-weight: 900;
}
.recovery-intro {
  margin: 0 0 24px;
  color: #57677b;
  font-size: 13px;
  line-height: 1.9;
}
.recovery-alert {
  margin-bottom: 18px;
  font-size: 12px;
  line-height: 1.7;
}
.recovery-form {
  display: grid;
  gap: 10px;
}
.recovery-form label {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 800;
}
.recovery-form :deep(.v-field) {
  border-radius: 11px;
}
.recovery-submit {
  margin-top: 16px;
  border-radius: 11px !important;
  background: var(--recovery-navy) !important;
  color: #fff !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.recovery-submit:hover {
  background: #102d50 !important;
}
.recovery-back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 23px;
  color: var(--recovery-navy);
  text-decoration: none;
  font-size: 12px;
  font-weight: 800;
}
.recovery-back:hover {
  text-decoration: underline;
}
.recovery-page[dir="rtl"] .recovery-back .v-icon {
  transform: rotate(180deg);
}

.recovery-page.dark-mode {
  color: #edf4fb;
  background: linear-gradient(180deg, #101c2d, #17273b);
}
.recovery-page.dark-mode .recovery-card {
  background: #1b2d42;
  border-color: #354b62;
}
.recovery-page.dark-mode .recovery-intro {
  color: #b7c8d9;
}
.recovery-page.dark-mode .recovery-emblem {
  color: #d7e8f8;
  background: #29435f;
}
.recovery-page.dark-mode .recovery-back {
  color: #bcd7f4;
}
.recovery-page.dark-mode .recovery-form :deep(.v-field) {
  color: #edf4fb;
}

@media (max-width: 600px) {
  .recovery-header {
    padding: 12px 16px;
  }
  .recovery-brand strong {
    font-size: 13px;
  }
  .recovery-brand small {
    font-size: 10px;
  }
  .recovery-brand-icon {
    flex-basis: 40px;
    height: 40px;
  }
  .recovery-language {
    padding: 6px 8px;
    font-size: 11px;
  }
  .recovery-main {
    padding: 22px 12px 40px;
    place-items: start center;
  }
  .recovery-card {
    padding: 22px 18px;
    border-radius: 17px;
  }
  .recovery-card h1 {
    font-size: 20px;
  }
}
</style>
